/**
 * Direct estimates and Taylor-linearization variances (brief 9.3, MET-02),
 * design effects (brief 9.4, MET-03) inputs, implemented behind the
 * computeDirectEstimates stub in engine.ts.
 *
 * Conventions established here (also recorded in validation/parity):
 * - Cell ids: the overall cell of an indicator is the indicator id; a
 *   subgroup cell is `${indicatorId}|${variable}|${level}`.
 * - computeDirectEstimates also emits ONE STATEWIDE domain per cell, with
 *   areaId STATEWIDE_AREA_ID. Brief 9.3 requires statewide domains to be
 *   accumulated in the same pass (they feed the smoothed design effects of
 *   9.4 and the benchmarking targets of 9.13), and the estimates array is
 *   the only channel the public signatures provide for them:
 *   smoothDesignEffects reads them back out of the array.
 * - PSU identity is (stratum, psu): PSU codes may repeat across strata
 *   (brief QC-03). Records without a stratum share one implicit stratum;
 *   records without a PSU are each their own PSU (the single-stage,
 *   with-replacement fallback of brief ING-01).
 * - n_h is always the FULL design's PSU count in stratum h; PSUs with no
 *   domain members contribute u = 0 to both stratum sums (brief 9.3).
 * - When the design supplies per-stratum PSU population counts
 *   (DirectEstimateOptions.psuPopulationCounts), each statewide domain
 *   also carries fpcVariance: the stepwise first-stage-FPC variance —
 *   (1 - f_h) times each stratum's with-replacement contribution, with
 *   certainty strata (f_h >= 1) contributing their second-stage
 *   within-PSU variance instead (Tier 2 Item 2 package P2, Decision Log
 *   Addendum 29; the construction pairing-verified as stepB + stepC in
 *   validation/simulation/src/tier2levers.ts). It feeds only the
 *   'state' design-effect basis in deff.ts. Per-domain variances are
 *   never FPC-adjusted (study E-4 found the per-area FPC delta harmful;
 *   decision C-10).
 * - When DirectEstimateOptions.areaFpcVariance is set (threaded by the
 *   analysis layer only, for the 'within-area' design-effect method —
 *   candidate W, Decision Log Addendum 50 Item 1), the same stepwise
 *   variance is additionally computed for EVERY domain, restricted to
 *   the domain's own strata and members, so the transform stage can
 *   form each area's own first-stage factor FPC_d = fpcVariance /
 *   variance. The statewide value is computed by the identical code
 *   path and is unchanged; with the option absent nothing here differs
 *   from the shipped computation.
 *
 * Lonely-PSU strata (n_h = 1), matching the R survey package exactly
 * (verified numerically against survey 4.5; see validation/parity):
 * - 'adjust': the stratum contributes u^2. survey's 'adjust' re-centres the
 *   lonely PSU total at the grand mean of PSU totals, and for a ratio
 *   (domain) mean the grand mean of the linearized PSU totals u_hjd is
 *   identically 0, so the contribution is the squared PSU total itself.
 * - 'average': the sum of the non-lonely strata's contributions is scaled
 *   by H / H_ok -- survey's onestage drops the lonely strata's NA variance
 *   matrices and rescales by nstrat/nokstrat. Because svymean casewise
 *   deletes rows missing the variable, H counts only strata containing at
 *   least one domain member, and H_ok those among them with n_h >= 2 in
 *   the full design. (With H over the full-design roster instead, the
 *   a2 domain of the direct.lonely-average fixture would be off by
 *   exactly 4/3.) If every member stratum is lonely the variance is NaN,
 *   as in survey.
 * - 'remove': the stratum contributes 0 (survey's 'certainty' is the same).
 *
 * Intervals (brief 9.3): logit with the standard normal quantile z by
 * default; Korn-Graubard when phat_d is 0 or 1. Note that at phat_d in
 * {0, 1} every u_hjd is 0, so Vhat_d = 0 and n_star = n_d, exactly the
 * case the brief's parenthetical covers.
 *
 * Quantile conventions (directive C-4, decision D-5; clarified in
 * docs/E5_CLARIFICATIONS_DRAFT.md item (d)): the logit interval uses
 * the standard normal quantile z — the brief does not name a quantile
 * family for it, and z is retained deliberately: every validated
 * coverage figure for the direct interval was produced under z, and
 * the model-side intervals are normal-theory as well, so the two
 * interval families stay comparable. A t-quantile option for the
 * logit interval is not offered. The Korn-Graubard interval is the
 * exception by construction: it uses t quantiles in the form the
 * brief's formula carries (the F-type ratio of the scaled Wilson
 * form implemented in kornGraubardInterval below), with the domain
 * degrees of freedom df_d = PSUs - strata (floored at 1, flagged
 * DF_FLOOR). The p-hat in {0, 1} convention — logit unavailable, KG
 * with n_star = n_d because Vhat_d = 0 — is pinned by the
 * direct.boundary parity fixture; its expected values are computed
 * from the brief 9.3 formulas by the generator's reference oracle
 * (validation/parity/oracle/direct_oracle.R; survey::svymean
 * supplies the estimates and variances), because survey::svyciprop
 * does not implement 9.3 (its logit path uses t critical values and
 * its "beta" path ignores the degrees of freedom; OPEN_QUESTIONS
 * Q4, docs/E5_CLARIFICATIONS_DRAFT.md item (e)).
 */
import { betaQuantile, inverseLogit, logit, normalQuantile, tQuantile } from './numeric.js';
/** areaId carried by the statewide domain emitted for every cell. */
export const STATEWIDE_AREA_ID = '__statewide__';
const SINGLE_STRATUM = '__single_stratum__';
/** Issue code used when a Korn-Graubard domain's df_d < 1 is floored to 1 (brief 9.3). */
export const DF_FLOOR_FLAG_CODE = 'DF_FLOOR';
function cellDefsFor(indicators, subgroups) {
    const defs = [];
    for (const indicatorId of indicators) {
        defs.push({ cellId: indicatorId, indicatorId });
        for (const spec of subgroups ?? []) {
            for (const level of spec.levels) {
                defs.push({
                    cellId: `${indicatorId}|${spec.variable}|${String(level.value)}`,
                    indicatorId,
                    subgroupVariable: spec.variable,
                    subgroupLevel: level.value,
                });
            }
        }
    }
    return defs;
}
function stratumKeyOf(record) {
    return record.stratum === undefined || record.stratum === null
        ? SINGLE_STRATUM
        : String(record.stratum);
}
function psuKeyOf(record, rowIndex) {
    const psu = record.psu === undefined || record.psu === null
        ? `__row_${rowIndex}`
        : String(record.psu);
    return `${stratumKeyOf(record)}|${psu}`;
}
function subgroupMatches(record, def) {
    if (def.subgroupVariable === undefined)
        return true;
    const value = record.subgroups[def.subgroupVariable];
    if (value === null || value === undefined)
        return false;
    return value === def.subgroupLevel || String(value) === String(def.subgroupLevel);
}
export function computeDirectEstimatesImpl(respondents, options) {
    if (options.fpc && !options.psuPopulationCounts) {
        // A finite population correction needs the design's PSU population
        // counts. Without them, refuse loudly rather than silently returning
        // with-replacement variances (see OPEN_QUESTIONS). When counts ARE
        // supplied, the correction is realized through the statewide
        // design-effect basis (DirectEstimate.fpcVariance, deff.ts 'state'
        // method); per-domain variances stay with-replacement (E-4, C-10).
        throw new Error('computeDirectEstimates: fpc is not supported — no PSU population counts exist in the input contract (brief 9.3)');
    }
    const alpha = 1 - options.confidence;
    const z = normalQuantile(1 - alpha / 2);
    // Full-design roster: stratum -> set of PSU keys (QC-03 composite keys).
    const roster = new Map();
    respondents.forEach((record, i) => {
        const s = stratumKeyOf(record);
        let set = roster.get(s);
        if (!set) {
            set = new Set();
            roster.set(s, set);
        }
        set.add(psuKeyOf(record, i));
    });
    let stratumFactors;
    if (options.psuPopulationCounts) {
        stratumFactors = new Map();
        for (const [sKey, psuSet] of roster) {
            const population = options.psuPopulationCounts.get(sKey);
            if (population === undefined || !(population > 0)) {
                stratumFactors.set(sKey, { oneMinusF: 1, certainty: false });
            }
            else {
                const f = psuSet.size / population;
                stratumFactors.set(sKey, {
                    oneMinusF: 1 - f,
                    certainty: f >= 1,
                });
            }
        }
    }
    const estimates = [];
    const defs = cellDefsFor(options.indicators, options.subgroups);
    // One pass per indicator over the respondents, accumulating every cell of
    // that indicator (overall, one per subgroup level) and every area domain
    // plus the statewide domain simultaneously (brief 9.3).
    for (const indicatorId of options.indicators) {
        const indicatorDefs = defs.filter((d) => d.indicatorId === indicatorId);
        const domains = new Map();
        const areaOrder = [];
        const seenAreas = new Set();
        const accumFor = (def, areaId) => {
            const mapKey = `${def.cellId}${areaId}`;
            let acc = domains.get(mapKey);
            if (!acc) {
                acc = {
                    key: {
                        cellId: def.cellId,
                        indicatorId: def.indicatorId,
                        subgroupVariable: def.subgroupVariable,
                        subgroupLevel: def.subgroupLevel,
                        areaId,
                    },
                    n: 0,
                    yesCount: 0,
                    weightedTotal: 0,
                    weightedYes: 0,
                    psu: new Map(),
                    memberPsus: new Set(),
                    memberStrata: new Set(),
                    psuMembers: new Map(),
                };
                domains.set(mapKey, acc);
            }
            return acc;
        };
        respondents.forEach((record, rowIndex) => {
            const outcome = record.outcomes[indicatorId];
            if (outcome === null || outcome === undefined)
                return; // excluded from this indicator (brief 9.1)
            const y = outcome === 1 ? 1 : 0;
            const w = record.weight;
            const sKey = stratumKeyOf(record);
            const pKey = psuKeyOf(record, rowIndex);
            for (const def of indicatorDefs) {
                if (!subgroupMatches(record, def))
                    continue;
                for (const areaId of [record.areaId, STATEWIDE_AREA_ID]) {
                    const acc = accumFor(def, areaId);
                    acc.n += 1;
                    acc.yesCount += y;
                    acc.weightedTotal += w;
                    acc.weightedYes += w * y;
                    let pa = acc.psu.get(pKey);
                    if (!pa) {
                        pa = { y: 0, n: 0 };
                        acc.psu.set(pKey, pa);
                    }
                    pa.y += w * y;
                    pa.n += w;
                    if (stratumFactors?.get(sKey)?.certainty === true &&
                        (areaId === STATEWIDE_AREA_ID || options.areaFpcVariance === true)) {
                        let members = acc.psuMembers.get(pKey);
                        if (!members) {
                            members = [];
                            acc.psuMembers.set(pKey, members);
                        }
                        members.push({ w, y });
                    }
                    acc.memberPsus.add(pKey);
                    acc.memberStrata.add(sKey);
                }
                if (!seenAreas.has(record.areaId)) {
                    seenAreas.add(record.areaId);
                    areaOrder.push(record.areaId);
                }
            }
        });
        // Emit domains cell by cell: areas in first-appearance order, statewide last.
        for (const def of indicatorDefs) {
            for (const areaId of [...areaOrder, STATEWIDE_AREA_ID]) {
                const acc = domains.get(`${def.cellId}${areaId}`);
                if (!acc || acc.n === 0 || acc.weightedTotal === 0)
                    continue;
                estimates.push(finalizeDomain(acc, roster, options.lonelyPsu, z, alpha, stratumFactors, options.areaFpcVariance === true));
            }
        }
    }
    return estimates;
}
function finalizeDomain(acc, roster, lonelyPsu, z, alpha, stratumFactors, areaFpcVariance = false) {
    const phat = acc.weightedYes / acc.weightedTotal;
    const nhat = acc.weightedTotal;
    // Linearized PSU totals u_hjd over the full design roster (brief 9.3).
    let variance = 0;
    let lonelySquares = 0;
    let lonelyStrata = 0;
    for (const [sKey, psuSet] of roster) {
        const nh = psuSet.size;
        let s1 = 0;
        let s2 = 0;
        for (const pKey of psuSet) {
            const pa = acc.psu.get(pKey);
            const u = pa ? (pa.y - phat * pa.n) / nhat : 0;
            s1 += u;
            s2 += u * u;
        }
        if (nh >= 2) {
            // max(0, ...) guards the mathematically non-negative bracket against
            // floating-point cancellation when every u in the stratum is equal.
            variance += (nh / (nh - 1)) * Math.max(0, s2 - (s1 * s1) / nh);
        }
        else {
            lonelyStrata += 1;
            lonelySquares += s1 * s1; // the stratum's single PSU has u = s1
        }
    }
    if (lonelyStrata > 0) {
        if (lonelyPsu === 'adjust') {
            variance += lonelySquares;
        }
        else if (lonelyPsu === 'average') {
            // survey's nstrat/nokstrat rescale, over strata with >= 1 domain
            // member (see the header note): memberless strata never enter
            // svymean's casewise-deleted variance computation.
            let memberOk = 0;
            for (const sKey of acc.memberStrata) {
                if ((roster.get(sKey)?.size ?? 0) >= 2)
                    memberOk += 1;
            }
            const memberStrata = acc.memberStrata.size;
            if (memberStrata > memberOk) {
                variance = memberOk > 0 ? variance * (memberStrata / memberOk) : NaN;
            }
        }
        // 'remove': lonely strata contribute 0.
    }
    if (variance < 0)
        variance = 0;
    // Stepwise first-stage-FPC variance, when the design frame supplied
    // PSU population counts (Tier 2 Item 2 package P2; the construction
    // pairing-verified as stepB + stepC in
    // validation/simulation/src/tier2levers.ts): each stratum contributes
    // (1 - f_h) times its with-replacement contribution, except a
    // certainty stratum (f_h >= 1), which contributes its second-stage
    // within-PSU variance. Strata are visited in sorted key order; the
    // PSU roster is the full design roster, so a PSU with no domain
    // members still counts toward its stratum's t with total 0. Computed
    // for statewide domains, and for every domain when areaFpcVariance
    // is set (candidate W's per-area factor; the statewide computation
    // is the identical code path either way).
    let fpcVariance;
    if (stratumFactors &&
        (acc.key.areaId === STATEWIDE_AREA_ID || areaFpcVariance)) {
        let total = 0;
        const keys = [...roster.keys()].sort();
        for (const sKey of keys) {
            const factor = stratumFactors.get(sKey) ?? { oneMinusF: 1, certainty: false };
            const psuSet = roster.get(sKey);
            if (factor.certainty) {
                // Certainty stratum: certaintyStratumContribution of the
                // harness construction — per sampled PSU j,
                // (m_j / (m_j - 1)) * sum_k (z_k - zbar_j)^2 over the domain
                // members in that PSU, z_k = w_k (y_k - phat) / Nhat; PSUs
                // with fewer than 2 members contribute 0.
                let certTotal = 0;
                for (const pKey of psuSet) {
                    const members = acc.psuMembers.get(pKey);
                    if (!members || members.length < 2)
                        continue;
                    const m = members.length;
                    const zs = members.map((mem) => (mem.w * (mem.y - phat)) / nhat);
                    const mean = zs.reduce((a, b) => a + b, 0) / m;
                    let ss = 0;
                    for (const zk of zs)
                        ss += (zk - mean) * (zk - mean);
                    certTotal += (m / (m - 1)) * ss;
                }
                total += certTotal;
            }
            else {
                // With-replacement stratum contribution (the harness's
                // stratumWrContribution): linearized totals
                // u = (Y - phat * N) / Nhat over the full roster; t >= 2 PSUs
                // give (t / (t - 1)) * max(0, s2 - s1^2 / t); a lonely PSU
                // gives s1^2 (the 'adjust' treatment, independent of the
                // domain variance's lonelyPsu option).
                const t = psuSet.size;
                let s1 = 0;
                let s2 = 0;
                for (const pKey of psuSet) {
                    const pa = acc.psu.get(pKey);
                    const u = pa ? (pa.y - phat * pa.n) / nhat : 0;
                    s1 += u;
                    s2 += u * u;
                }
                const contribution = t >= 2 ? (t / (t - 1)) * Math.max(0, s2 - (s1 * s1) / t) : s1 * s1;
                total += factor.oneMinusF * contribution;
            }
        }
        fpcVariance = total;
    }
    const standardError = Math.sqrt(variance);
    const psuCount = acc.memberPsus.size;
    const domainStrataCount = acc.memberStrata.size;
    const degreesOfFreedom = psuCount - domainStrataCount;
    const flags = [];
    let interval;
    if (phat === 0 || phat === 1) {
        interval = kornGraubardInterval(phat, variance, acc.n, degreesOfFreedom, alpha, flags, acc.key);
    }
    else if (standardError === 0) {
        interval = { lower: phat, upper: phat, method: 'logit' };
    }
    else {
        const half = (z * standardError) / (phat * (1 - phat));
        const centre = logit(phat);
        interval = {
            lower: inverseLogit(centre - half),
            upper: inverseLogit(centre + half),
            method: 'logit',
        };
    }
    return {
        domain: acc.key,
        n: acc.n,
        yesCount: acc.yesCount,
        weightedTotal: acc.weightedTotal,
        estimate: phat,
        variance,
        ...(fpcVariance !== undefined ? { fpcVariance } : {}),
        standardError,
        degreesOfFreedom,
        psuCount,
        strataCount: domainStrataCount,
        interval,
        flags,
    };
}
/**
 * The Korn-Graubard interval bounds of brief 9.3 for a direct
 * estimate, with no issue side-effects: the pure numerical core of
 * kornGraubardInterval below. The degrees of freedom are floored at
 * 1 exactly as the interval constructor does (its DF_FLOOR issue is
 * raised there, not here). Exported for the disclosure rules: the
 * proportion presentation standard (Parker et al. 2017) defines its
 * interval-width tests on this interval for complex-survey direct
 * estimates, so the pipelines compute its width for every
 * direct-reported domain (Tier 2 Item 6, Decision Log Addendum 12
 * residual (a)). The reported interval of a domain is unaffected:
 * it stays the brief 9.3 interval.
 */
export function kornGraubardBounds(phat, variance, n, degreesOfFreedom, alpha) {
    // Brief 9.3: n_star = phat(1-phat)/Vhat capped at n_d; n_d when Vhat = 0.
    const nStar = variance > 0 ? Math.min((phat * (1 - phat)) / variance, n) : n;
    const dfUsed = Math.max(degreesOfFreedom, 1);
    // The t-ratio adjustment needs n_d - 1 >= 1; for a one-respondent
    // domain the ratio is undefined and the unadjusted n_star is used.
    let nAdj = nStar;
    if (n - 1 >= 1) {
        const tNum = tQuantile(1 - alpha / 2, n - 1);
        const tDen = tQuantile(1 - alpha / 2, dfUsed);
        nAdj = nStar * (tNum / tDen) * (tNum / tDen);
    }
    const xAdj = phat * nAdj;
    const lower = xAdj === 0 ? 0 : betaQuantile(alpha / 2, xAdj, nAdj - xAdj + 1);
    const upper = xAdj === nAdj ? 1 : betaQuantile(1 - alpha / 2, xAdj + 1, nAdj - xAdj);
    return { lower, upper };
}
function kornGraubardInterval(phat, variance, n, degreesOfFreedom, alpha, flags, domain) {
    if (degreesOfFreedom < 1) {
        flags.push({
            code: DF_FLOOR_FLAG_CODE,
            severity: 'warning',
            message: 'Domain degrees of freedom below 1; the Korn-Graubard interval used df = 1 (brief 9.3).',
            domain,
        });
    }
    // The t-ratio adjustment needs n_d - 1 >= 1; for a one-respondent domain
    // the ratio is undefined and the unadjusted n_star is used (flagged with
    // the same code). See validation/parity/OPEN_QUESTIONS.md.
    const nStar = variance > 0 ? Math.min((phat * (1 - phat)) / variance, n) : n;
    if (n - 1 < 1 && nStar === n) {
        flags.push({
            code: DF_FLOOR_FLAG_CODE,
            severity: 'warning',
            message: 'Single-respondent domain; the Korn-Graubard t adjustment is undefined and n_adj = n_star was used.',
            domain,
        });
    }
    const { lower, upper } = kornGraubardBounds(phat, variance, n, degreesOfFreedom, alpha);
    return { lower, upper, method: 'korn-graubard' };
}
//# sourceMappingURL=direct.js.map